import type { Labelled } from './common.js';

/**
 * ثوابت وحدة المبيعات.
 */

/* ==========================================================================
   عروض الأسعار
   ========================================================================== */

export const QUOTATION_STATUSES = [
  'DRAFT',
  'SENT',
  'NEGOTIATION',
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
] as const;
export type QuotationStatus = (typeof QUOTATION_STATUSES)[number];

export interface QuotationStatusDef extends Labelled<QuotationStatus> {
  /** حالة نهائية لا يُعدَّل بعدها العرض. */
  terminal: boolean;
}

export const QUOTATION_STATUS_DEFS: readonly QuotationStatusDef[] = [
  { key: 'DRAFT', label: 'مسودة', kind: 'neutral', terminal: false },
  { key: 'SENT', label: 'تم الإرسال', kind: 'info', terminal: false },
  /*
   * بين الإرسال والقرار مسافةٌ يعيشها البائع.
   *
   * «أُرسل» و«مقبول» طرفان، وبينهما الأسبوعُ الذي يُتابَع فيه العرض:
   * مكالمةٌ تُذكّر، وسؤالٌ عن بندٍ، وتعديلٌ يُطلب. جمعُ ذلك كلِّه في
   * «أُرسل» يخفي أين يقف العملُ فعلًا، ويجعل عرضًا يُتابَع كعرضٍ
   * أُرسل ونُسي — وهما ليسا سواءً في لوحةٍ تُقرأ بنظرة.
   */
  { key: 'NEGOTIATION', label: 'قيد المتابعة', kind: 'warning', terminal: false },
  { key: 'ACCEPTED', label: 'مقبول', kind: 'success', terminal: true },
  { key: 'REJECTED', label: 'مرفوض', kind: 'danger', terminal: true },
  { key: 'EXPIRED', label: 'منتهي الصلاحية', kind: 'neutral', terminal: true },
] as const;

export const QUOTATION_STATUS_MAP = Object.fromEntries(
  QUOTATION_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<QuotationStatus, QuotationStatusDef>>;

/* ==========================================================================
   العقود
   ========================================================================== */

export const CONTRACT_STATUSES = ['DRAFT', 'ACTIVE', 'COMPLETED', 'CANCELLED'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const CONTRACT_STATUS_DEFS: readonly Labelled<ContractStatus>[] = [
  { key: 'DRAFT', label: 'مسودة', kind: 'neutral' },
  { key: 'ACTIVE', label: 'ساري', kind: 'success' },
  { key: 'COMPLETED', label: 'منتهٍ', kind: 'info' },
  { key: 'CANCELLED', label: 'ملغي', kind: 'danger' },
] as const;

export const CONTRACT_STATUS_MAP = Object.fromEntries(
  CONTRACT_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ContractStatus, Labelled<ContractStatus>>>;

/* ==========================================================================
   الفواتير والتحصيل
   ========================================================================== */

export const INVOICE_STATUSES = [
  'DRAFT',
  'SENT',
  'PARTIAL',
  'PAID',
  'OVERDUE',
  'CANCELLED',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_STATUS_DEFS: readonly Labelled<InvoiceStatus>[] = [
  { key: 'DRAFT', label: 'مسودة', kind: 'neutral' },
  { key: 'SENT', label: 'مُرسَلة', kind: 'info' },
  { key: 'PARTIAL', label: 'مسدَّدة جزئيًا', kind: 'warning' },
  { key: 'PAID', label: 'مسدَّدة بالكامل', kind: 'success' },
  { key: 'OVERDUE', label: 'متأخرة', kind: 'danger' },
  { key: 'CANCELLED', label: 'ملغاة', kind: 'neutral' },
] as const;

export const INVOICE_STATUS_MAP = Object.fromEntries(
  INVOICE_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<InvoiceStatus, Labelled<InvoiceStatus>>>;

/**
 * رمزُ رابط الفاتورة للعميل: معرّفُ الفاتورة ثمّ توقيعُها (HMAC بستّ عشرة
 * بايتًا ستّ عشريّة) — «ck…-9f3a…». يُفحص شكلُه قبل أيّ حساب، والتوقيعُ
 * مربوطٌ بالشركة والفاتورة معًا فلا يُخمَّن ولا يُنقل إلى شركةٍ أخرى.
 */
export const INVOICE_SHARE_TOKEN_PATTERN = /^[a-z0-9]{20,32}-[0-9a-f]{32}$/;

/** مسارُ الفاتورة العامّ في الواجهة — يفتحه العميلُ بلا حساب. */
export function invoiceSharePath(token: string): string {
  return `/v/invoice/${token}`;
}

/**
 * فترات تقادم الذمم.
 *
 * تقسيم المستحقات حسب طول التأخير هو ما يحوّل رقمًا إجماليًا إلى قرار:
 * مئة ألف مستحقة منذ أسبوع ليست كمئة ألف مستحقة منذ أربعة أشهر.
 */
export interface AgingBucket {
  key: string;
  label: string;
  minDays: number;
  maxDays: number;
  kind: Labelled<string>['kind'];
}

export const AGING_BUCKETS: readonly AgingBucket[] = [
  { key: 'current', label: 'أقل من 30 يومًا', minDays: 0, maxDays: 29, kind: 'success' },
  { key: 'b30', label: 'من 30 إلى 60', minDays: 30, maxDays: 60, kind: 'info' },
  { key: 'b60', label: 'من 61 إلى 90', minDays: 61, maxDays: 90, kind: 'warning' },
  { key: 'b90', label: 'أكثر من 90 يومًا', minDays: 91, maxDays: 99_999, kind: 'danger' },
] as const;

/* ==========================================================================
   إعدادات افتراضية
   ========================================================================== */

export const SALES_DEFAULTS = {
  /** نسبة ضريبة القيمة المضافة في مصر. يضبطها كل مستأجر من الإعدادات. */
  taxRate: 14,
  /** مدة صلاحية عرض السعر بالأيام. */
  quotationValidityDays: 14,
  /** مهلة سداد الفاتورة بالأيام. */
  invoiceDueDays: 30,
  currency: 'EGP',
  currencyLabel: 'ج.م',
} as const;

/**
 * بادئات ترقيم المستندات.
 *
 * لكل نوع تسلسل مستقل: خلط الفواتير بالقيود في تسلسل واحد يجعل أرقام
 * الفواتير متقطّعة، وهو ما لا تقبله مراجعة محاسبية.
 */
export const DOCUMENT_PREFIXES = {
  quotation: 'QUO',
  /* الكتالوجُ يُرقَّم كما تُرقَّم المستندات: تسلسلٌ ذرّيٌّ لا عدُّ صفوف. */
  service: 'SRV',
  package: 'PKG',
  invoice: 'INV',
  payment: 'PAY',
  contract: 'CTR',
  journal: 'JV',
  bill: 'BIL',
  billPayment: 'BPY',
  expense: 'EXP',
  /*
   * التوظيفُ يُرقَّم كما تُرقَّم المستندات.
   *
   * كودُ المرشّح يُنطق في المكالمة ويُكتب على الملفّ الورقي، فلا يصلح أن
   * يكون معرّفًا عشوائيًّا من عشرين حرفًا. والعدُّ بعدد الصفوف يعطي الرقم
   * نفسَه لطلبين وصلا في الثانية نفسها — وهو ما يقع فعلًا على رابطٍ علنيّ.
   */
  job: 'JOB',
  candidate: 'CND',
  jobOffer: 'JOF',
  /* عقدُ العمل — غيرُ عقد العميل 'CTR'، وتسلسلٌ مستقلٌّ لأنّهما لا يُخلطان. */
  jobContract: 'EMP',
  /*
   * حالةُ الموظّف — تُشار إليها برقمها في المراسلة وفي محضر التحقيق.
   */
  employeeCase: 'CAS',
} as const;

export type DocumentKind = keyof typeof DOCUMENT_PREFIXES;

/* ==========================================================================
   حساب إجماليات المستند
   ========================================================================== */

export interface LineInput {
  quantity: number;
  unitPrice: number;
  discount?: number;
}

export interface DocumentTotals {
  subtotal: number;
  discount: number;
  taxAmount: number;
  total: number;
  lineTotals: number[];
}

/**
 * حساب إجماليات المستند.
 *
 * موضعه في الحزمة المشتركة مقصود: الخادم يحسب القيمة المعتمدة، والواجهة
 * تعرضها لحظيًا قبل الحفظ. لو تكرّر المنطق في الموضعين فسيختلفان يومًا ما،
 * فيرى المستخدم رقمًا ويُحفظ غيره.
 *
 * الخادم لا يثق بما ترسله الواجهة على أي حال، لكن تطابق الحساب يمنع مفاجأة
 * تغيّر الرقم بعد الحفظ.
 */
export function computeTotals(
  lines: readonly LineInput[],
  options: { headerDiscount?: number; taxRate?: number } = {},
): DocumentTotals {
  const taxRate = options.taxRate ?? SALES_DEFAULTS.taxRate;
  const headerDiscount = options.headerDiscount ?? 0;

  const lineTotals = lines.map((line) => {
    const gross = round2(line.quantity * line.unitPrice);
    return round2(Math.max(0, gross - (line.discount ?? 0)));
  });

  const subtotal = round2(lineTotals.reduce((sum, v) => sum + v, 0));

  // الخصم لا يتجاوز المجموع، وإلا صار الوعاء الضريبي سالبًا.
  const discount = round2(Math.min(headerDiscount, subtotal));
  const taxable = round2(subtotal - discount);
  const taxAmount = round2((taxable * taxRate) / 100);
  const total = round2(taxable + taxAmount);

  return { subtotal, discount, taxAmount, total, lineTotals };
}

/**
 * التقريب إلى منزلتين.
 *
 * الجمع المتكرر لأرقام عشرية في الفاصلة العائمة يراكم فروقًا تظهر كقرش
 * زائد أو ناقص في الإجمالي، وهو ما يجعل الفاتورة لا تطابق بنودها.
 */
export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** عدد أيام التأخر عن السداد. القيمة السالبة تعني أن الموعد لم يحن. */
export function daysOverdue(dueAt: Date | string, now: Date = new Date()): number {
  const due = new Date(dueAt);
  const ms = now.getTime() - due.getTime();
  return Math.floor(ms / 86_400_000);
}

/** فترة التقادم التي تقع فيها فاتورة متأخرة. */
export function agingBucketFor(days: number): AgingBucket {
  return AGING_BUCKETS.find((b) => days >= b.minDays && days <= b.maxDays) ?? AGING_BUCKETS[0]!;
}

/* ==========================================================================
   أهداف المندوبين
   ========================================================================== */

/**
 * فتراتُ الهدف ثلاثٌ لا واحدة.
 *
 * السنويُّ يُقاس عليه توظيفٌ وتوسّع، والربعيُّ إيقاعُ الخطّة، والشهريُّ
 * ما يُحاسَب عليه المندوبُ في كشفه. وقسمةُ السنويّ على اثني عشر لا تُغني:
 * لا الشهورُ سواءٌ في البيع ولا الأرباع.
 */
export const SALES_TARGET_KINDS = ['YEAR', 'QUARTER', 'MONTH'] as const;
export type SalesTargetKind = (typeof SALES_TARGET_KINDS)[number];

export const SALES_TARGET_KIND_DEFS: readonly Labelled<SalesTargetKind>[] = [
  { key: 'YEAR', label: 'سنوي', kind: 'info' },
  { key: 'QUARTER', label: 'ربع سنوي', kind: 'neutral' },
  { key: 'MONTH', label: 'شهري', kind: 'neutral' },
] as const;

export const SALES_TARGET_KIND_MAP = Object.fromEntries(
  SALES_TARGET_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<SalesTargetKind, Labelled<SalesTargetKind>>>;

/** مفتاحُ الفترة كما يُخزَّن ويُقرأ: 2026 · 2026-Q3 · 2026-09. */
export function salesTargetKey(kind: SalesTargetKind, at: Date): string {
  const y = at.getFullYear();
  if (kind === 'YEAR') return String(y);
  if (kind === 'QUARTER') return `${y}-Q${Math.floor(at.getMonth() / 3) + 1}`;
  return `${y}-${String(at.getMonth() + 1).padStart(2, '0')}`;
}

/** اسمُ الفترة كما يُعرض: «سنة ٢٠٢٦» · «الربع الثالث ٢٠٢٦» · «سبتمبر ٢٠٢٦». */
export function salesTargetLabel(kind: SalesTargetKind, key: string): string {
  const months = [
    'يناير',
    'فبراير',
    'مارس',
    'أبريل',
    'مايو',
    'يونيو',
    'يوليو',
    'أغسطس',
    'سبتمبر',
    'أكتوبر',
    'نوفمبر',
    'ديسمبر',
  ];
  const quarters = ['الربع الأول', 'الربع الثاني', 'الربع الثالث', 'الربع الرابع'];
  if (kind === 'YEAR') return `سنة ${key}`;
  if (kind === 'QUARTER') {
    const [y, q] = key.split('-Q');
    return `${quarters[Number(q) - 1] ?? key} ${y}`;
  }
  const [y, m] = key.split('-');
  return `${months[Number(m) - 1] ?? key} ${y}`;
}

/* ==========================================================================
   خصمُ الخدمة في الكتالوج
   ========================================================================== */

export const SERVICE_DISCOUNT_KINDS = ['NONE', 'PERCENT', 'AMOUNT'] as const;
export type ServiceDiscountKind = (typeof SERVICE_DISCOUNT_KINDS)[number];

export const SERVICE_DISCOUNT_KIND_DEFS = [
  { key: 'NONE', label: 'بلا خصم' },
  { key: 'PERCENT', label: 'نسبة مئوية' },
  { key: 'AMOUNT', label: 'مبلغ ثابت' },
] as const;

export const SERVICE_DISCOUNT_KIND_MAP = Object.fromEntries(
  SERVICE_DISCOUNT_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ServiceDiscountKind, { key: string; label: string }>>;

export const SERVICE_EVENT_KINDS = [
  'CREATED',
  'PRICE',
  'COST',
  'DISCOUNT_SET',
  'DISCOUNT_CLEAR',
  'STATUS',
  'DETAILS',
] as const;
export type ServiceEventKind = (typeof SERVICE_EVENT_KINDS)[number];

export const SERVICE_EVENT_KIND_DEFS = [
  { key: 'CREATED', label: 'أُنشئت', kind: 'info' },
  { key: 'PRICE', label: 'تغيّر السعر', kind: 'warning' },
  { key: 'COST', label: 'تغيّرت التكلفة', kind: 'neutral' },
  { key: 'DISCOUNT_SET', label: 'وُضع خصم', kind: 'success' },
  { key: 'DISCOUNT_CLEAR', label: 'رُفع الخصم', kind: 'neutral' },
  { key: 'STATUS', label: 'تغيّرت الحالة', kind: 'warning' },
  { key: 'DETAILS', label: 'عُدِّلت البيانات', kind: 'neutral' },
] as const;

export const SERVICE_EVENT_KIND_MAP = Object.fromEntries(
  SERVICE_EVENT_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ServiceEventKind, { key: string; label: string; kind: string }>>;

export interface ServiceDiscountPolicy {
  unitPrice: number;
  discountKind: ServiceDiscountKind;
  discountValue: number | null;
  discountFrom: string | Date | null;
  discountTo: string | Date | null;
}

export interface ServicePrice {
  /** هل الخصمُ نافذٌ اليوم — لا مجرّد مكتوب. */
  active: boolean;
  /** ما يُحسم فعلًا من سعر الوحدة. */
  off: number;
  /** السعرُ بعد الحسم، وهو سعرُ الوحدة نفسُه إن لم يكن خصمٌ نافذ. */
  price: number;
  /** لماذا لم يَنفُذ: لم يبدأ بعد، أو انتهى. */
  state: 'none' | 'active' | 'upcoming' | 'expired';
}

/**
 * سعرُ الخدمة نافذًا لا مكتوبًا.
 *
 * الخصمُ له مدّة: مكتوبٌ قبل أوانه لا يُطبَّق، ومكتوبٌ بعد انقضائه لا
 * يُطبَّق. وحسابُه في مكانٍ واحدٍ يمنع أن يقرأ الكتالوجُ سعرًا وتقرأ
 * الفاتورةُ غيرَه.
 *
 * والحدّان شاملان: يومُ البداية ويومُ النهاية داخلان في المدّة — هكذا
 * يُفهم «من الأوّل إلى العاشر» عند من يكتبه.
 */
export function servicePrice(
  s: ServiceDiscountPolicy,
  at: Date = new Date(),
): ServicePrice {
  const base = Number(s.unitPrice) || 0;
  const value = s.discountValue == null ? 0 : Number(s.discountValue);

  if (s.discountKind === 'NONE' || value <= 0) {
    return { active: false, off: 0, price: base, state: 'none' };
  }

  const day = (v: string | Date | null): number | null => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime())
      ? null
      : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };
  const today = Date.UTC(at.getFullYear(), at.getMonth(), at.getDate());
  const from = day(s.discountFrom);
  const to = day(s.discountTo);

  if (from != null && today < from) {
    return { active: false, off: 0, price: base, state: 'upcoming' };
  }
  if (to != null && today > to) {
    return { active: false, off: 0, price: base, state: 'expired' };
  }

  /* لا يهبط السعرُ تحت الصفر مهما كُتب في الخصم. */
  const raw = s.discountKind === 'PERCENT' ? (base * value) / 100 : value;
  const off = Math.min(base, Math.round(raw * 100) / 100);

  return {
    active: off > 0,
    off,
    price: Math.round((base - off) * 100) / 100,
    state: off > 0 ? 'active' : 'none',
  };
}
